import Favorite from "@mui/icons-material/Favorite";
import VolunteerActivism from "@mui/icons-material/VolunteerActivism";
import { useState } from "react";
import type { GiftContext } from "@/interfaces/redemption";
import { htmlToParagraphs } from "@/utils/html-text-helper";

interface RedemptionPledgeBannerProps {
  /** The campaign's featured beneficiary — present is the caller's render gate. */
  beneficiary: NonNullable<GiftContext["beneficiary"]>;
  /** Sender — drives the "champion" (company name, else first name). */
  sender: GiftContext["sender"];
}

/** Word-boundary cut for the "About {charity}" body (matches the prototype). */
const ABOUT_LIMIT = 140;
const READ_TOGGLE =
  "relative ml-0.5 cursor-pointer font-lato text-[13px] font-semibold text-dust-ink hover:underline max-md:before:content-[''] max-md:before:absolute max-md:before:inset-x-0 max-md:before:-inset-y-2.75";

/**
 * "Donate unredeemed" charity call-out below the gift feed — shown when the sender chose
 * to donate the value of unclaimed gifts to a cause on campaign close (config.donateUnredeemed)
 * and a beneficiary is set. Ported from the prototype's #lpPledgeBanner to Tailwind + tokens.
 */
export default function RedemptionPledgeBanner({
  beneficiary,
  sender,
}: RedemptionPledgeBannerProps) {
  const [aboutOpen, setAboutOpen] = useState(false);

  // Champion = the sender's company when the account has one, else their first name
  // (matches the prototype's @SenderCompany → @SenderFirstName merge-tag fallback).
  const champion = sender.companyName || sender.firstName;
  // Rendered verbatim (a full sentence); a blank or whitespace-only value hides the row.
  const impactMetric = beneficiary.impactLine?.trim() ? beneficiary.impactLine : "";

  // The stored value is HTML: decode to plain paragraphs before the toggle's word-boundary cut
  // runs, so the cut can never land inside a tag or an entity.
  const aboutParagraphs = htmlToParagraphs(beneficiary.about);
  const aboutJoined = aboutParagraphs.join(" ");
  const aboutNeedsToggle = aboutJoined.length > ABOUT_LIMIT;
  const cut = aboutJoined.lastIndexOf(" ", ABOUT_LIMIT);
  const aboutTruncated = aboutNeedsToggle
    ? `${aboutJoined.slice(0, cut > 60 ? cut : ABOUT_LIMIT)}…`
    : aboutJoined;
  // Position-qualified so two identical paragraphs still get distinct React keys.
  const aboutEntries = aboutParagraphs.map((paragraph, index) => ({
    paragraph,
    key: `${index}-${paragraph}`,
  }));

  return (
    <section className="w-full border-t border-card-border bg-white px-5 pt-9 pb-13 md:px-6 md:pt-12 md:pb-24">
      <div className="mx-auto grid max-w-115 grid-cols-1 items-start gap-5 md:max-w-205 md:grid-cols-[260px_minmax(0,480px)] md:gap-10">
        {/* Beneficiary image (icon fallback when none) */}
        {beneficiary.imageUrl ? (
          <img
            src={beneficiary.imageUrl}
            alt={beneficiary.name}
            className="aspect-square w-full max-w-65 rounded-card object-cover md:h-65 md:w-65"
          />
        ) : (
          <span className="flex aspect-square w-full max-w-65 items-center justify-center rounded-card bg-light-dust md:h-65 md:w-65">
            <VolunteerActivism sx={{ fontSize: 64, color: "var(--color-dark-dust)" }} />
          </span>
        )}

        <div className="flex min-w-0 flex-col items-start gap-2.5">
          <h2 className="font-crimson text-[21px] font-normal leading-[1.3] text-rodeo-black md:text-2xl">
            {champion} is championing {beneficiary.name}
          </h2>

          {impactMetric && (
            <div className="mt-1 mb-0.5 flex items-center gap-3">
              {beneficiary.impactIconUrl ? (
                <img
                  src={beneficiary.impactIconUrl}
                  alt={`${beneficiary.name} impact`}
                  className="h-8 w-8 shrink-0 rounded-full object-cover outline-2 outline-offset-[3px] outline-card-border"
                />
              ) : (
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-light-dust text-dark-dust outline-2 outline-offset-[3px] outline-card-border">
                  <Favorite sx={{ fontSize: 18 }} />
                </span>
              )}
              <span className="font-lato text-sm leading-[1.4] text-rodeo-black">
                {impactMetric}
              </span>
            </div>
          )}

          <div className="font-lato text-[10px] font-bold uppercase tracking-[1.5px] text-dust-ink">
            Your gift does more
          </div>
          <p className="font-lato text-[15px] leading-[1.7] text-graphite">
            {champion} has chosen to support {beneficiary.name} through this gift experience. The
            value of any unclaimed gifts will be redirected as a charitable contribution to this
            cause.
          </p>

          {aboutParagraphs.length > 0 && (
            <div className="mt-2.5 border-t border-smoke pt-4">
              <div className="mb-2 font-lato text-[11px] font-bold uppercase tracking-[1.5px] text-dust-ink">
                About {beneficiary.name}
              </div>
              {aboutNeedsToggle && !aboutOpen ? (
                <p className="font-lato text-sm leading-[1.7] text-graphite">
                  {aboutTruncated}{" "}
                  <button type="button" onClick={() => setAboutOpen(true)} className={READ_TOGGLE}>
                    Read more
                  </button>
                </p>
              ) : (
                <>
                  {aboutEntries.map(({ paragraph, key }) => (
                    <p
                      key={key}
                      className="font-lato text-sm leading-[1.7] text-graphite mt-2 first:mt-0"
                    >
                      {paragraph}
                    </p>
                  ))}
                  {aboutNeedsToggle && (
                    <button
                      type="button"
                      onClick={() => setAboutOpen(false)}
                      className={READ_TOGGLE}
                    >
                      Read less
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
